import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { MagnifyingGlassIcon, PlusIcon, PencilIcon, TrashIcon, PhotoIcon } from '@heroicons/react/24/outline';

interface Ingredient {
  _id: string;
  name_en: string;
  name_ar: string;
  category?: { _id: string; name_en?: string; name_ar?: string } | string;
  default_unit?: { en: string; ar: string };
  always_available?: boolean;
  image?: string;
}

export default function IngredientsTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [categories, setCategories] = useState<{ _id: string; name_en: string; name_ar: string }[]>([]);

  const loadCategories = async () => {
    try {
      const res = await api.get('/ingredients/categories');
      const data = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
      setCategories(data);
    } catch (_) {
      setCategories([]);
    }
  };

  const loadIngredients = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (activeSearch) params.search = activeSearch;
      if (categoryFilter && categoryFilter !== 'all') params.category = categoryFilter;
      const res = await api.get('/ingredients', { params });
      const data = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
      setIngredients(data);
    } catch (err) {
      console.error(err);
      setIngredients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadIngredients();
  }, [activeSearch, categoryFilter]);

  const handleSearch = () => setActiveSearch(search);

  const handleDelete = async (id: string) => {
    if (!confirm(t('pantryIngredients.confirmDelete'))) return;
    try {
      await api.delete(`/ingredients/${id}`);
      loadIngredients();
    } catch (err: any) {
      alert(err.response?.data?.message || t('pantryIngredients.failedToDelete'));
    }
  };

  const getCategoryName = (ing: Ingredient) => {
    const cat = ing.category;
    if (!cat) return '-';
    if (typeof cat === 'string') return cat;
    return cat.name_en || cat.name_ar || '-';
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('pantryIngredients.title')}</h3>
            <p className="text-sm text-gray-500">{t('pantryIngredients.description')}</p>
          </div>
          <button
            onClick={() => navigate('/pantry-ingredients/new')}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600"
          >
            <PlusIcon className="w-5 h-5" />
            <span>{t('pantryIngredients.addIngredient')}</span>
          </button>
        </div>
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px] relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('pantryIngredients.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2"
          >
            <option value="all">{t('pantryIngredients.allCategories')}</option>
            {categories.map((c) => (
              <option key={c._id} value={c._id}>{c.name_en}</option>
            ))}
          </select>
          <button
            onClick={handleSearch}
            className="px-6 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600"
          >
            {t('common.search')}
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-gray-500">{t('pantryIngredients.loading')}</div>
        ) : ingredients.length === 0 ? (
          <div className="p-8 text-center text-gray-500">{t('pantryIngredients.noIngredients')}</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">{t('pantryIngredients.icon')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('pantryIngredients.nameEn')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('pantryIngredients.nameAr')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('pantryIngredients.category')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('pantryIngredients.defaultUnitEn')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {ingredients.map((ing) => (
                <tr key={ing._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    {ing.image ? (
                      <img
                        src={ing.image}
                        alt=""
                        className="w-10 h-10 rounded object-cover border border-gray-200 bg-gray-100"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded border border-gray-200 bg-gray-100 flex items-center justify-center">
                        <PhotoIcon className="w-5 h-5 text-gray-400" />
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{ing.name_en || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600" dir="rtl">{ing.name_ar || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{getCategoryName(ing)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {ing.default_unit?.en || '-'} / <span dir="rtl">{ing.default_unit?.ar || '-'}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => navigate(`/pantry-ingredients/${ing._id}/edit`)}
                        className="text-primary-orange hover:text-orange-600"
                        title={t('common.edit')}
                      >
                        <PencilIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(ing._id)}
                        className="text-red-600 hover:text-red-800"
                        title={t('common.delete')}
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
